import { TFunction } from "i18next";

import { ITriggeredAlertSummaryParts } from "../@types/Alerts";
import { persistedCopyT } from "../config/persistedCopy";
import { TRIGGER_TYPE_LABEL_KEYS } from "./triggerLabels";

const COMPARATOR_KEYS: Record<string, string> = {
  ">": "patientPage_alerts_summary_comparator_greaterThan",
  "<": "patientPage_alerts_summary_comparator_lessThan",
  ">=": "patientPage_alerts_summary_comparator_greaterOrEqual",
  "<=": "patientPage_alerts_summary_comparator_lessOrEqual",
};

const has = (keys: Record<string, string>, value: string | undefined) =>
  value !== undefined && Object.prototype.hasOwnProperty.call(keys, value);

/**
 * A measurement's name inside the sentence. The API prints its own English names, a few of which
 * differ from the portal's labels ("Pulse OX" against "Oxygen Saturation"); the portal's label is
 * only used where its English is the API's name, so the English sentence stays word for word.
 */
const measurementName = (
  translate: TFunction,
  field: string | undefined,
  apiName: string
) => {
  const key = has(TRIGGER_TYPE_LABEL_KEYS, field)
    ? TRIGGER_TYPE_LABEL_KEYS[field as string]
    : undefined;

  return key && persistedCopyT(key) === apiName ? translate(key) : apiName;
};

/** The sentence in `translate`'s language, or undefined for a shape the keys do not cover. */
function renderSummary(
  translate: TFunction,
  parts: ITriggeredAlertSummaryParts
): string | undefined {
  if (parts.weightGain) {
    const { value, unit, days } = parts.weightGain;

    if (value === undefined || days === undefined || days === null) {
      return undefined;
    }

    return translate("patientPage_alerts_summary_weightGain", {
      count: days,
      value: `${value}${unit ?? ""}`,
    });
  }

  const conditions = parts.conditions ?? [];

  if (conditions.length < 1 || conditions.length > 2) return undefined;

  const renderClause = (
    clause: NonNullable<ITriggeredAlertSummaryParts["conditions"]>[number]
  ): string | undefined => {
    if (!clause.measurementName || !has(COMPARATOR_KEYS, clause.comparator)) {
      return undefined;
    }

    const name = measurementName(
      translate,
      clause.measurementField,
      clause.measurementName
    );
    const measurement = translate(
      parts.highest
        ? "patientPage_alerts_summary_measurementHighest"
        : "patientPage_alerts_summary_measurementAny",
      { type: name }
    );
    const comparator = translate(COMPARATOR_KEYS[clause.comparator as string]);

    if (clause.percentChange !== undefined && clause.percentChange !== null) {
      return translate("patientPage_alerts_summary_conditionPercent", {
        measurement,
        percent: clause.percentChange,
        comparator,
      });
    }

    if (clause.threshold !== undefined && clause.threshold !== null) {
      return translate("patientPage_alerts_summary_conditionThreshold", {
        measurement,
        comparator,
        value: `${clause.threshold}${clause.unit ?? ""}`,
      });
    }

    return undefined;
  };

  const rendered = conditions.map(renderClause);

  if (rendered.some((clause) => clause === undefined)) return undefined;

  const clauses = rendered as string[];

  let condition =
    clauses.length === 2
      ? translate("patientPage_alerts_summary_and", {
          first: clauses[0],
          second: clauses[1],
        })
      : clauses[0];

  if (parts.consecutiveDays !== undefined && parts.consecutiveDays !== null) {
    condition = translate("patientPage_alerts_summary_consecutiveDays", {
      condition,
      days: parts.consecutiveDays,
    });
  }

  return translate("patientPage_alerts_summary_sentence", { condition });
}

/**
 * EN-5284: an alert's summary in the user's language.
 *
 * The API builds the sentence in English (the same wording as the alert notification) and sends
 * the pieces next to it. The portal renders the pieces, and only shows its rendering when its
 * en-GB version is exactly the API's sentence — any other summary (a workflow alert's, an older
 * API without the pieces, a wording the keys do not cover) is shown as the API sent it. Display
 * only: nothing is stored or sent.
 */
export function formatTriggeredAlertSummary(
  t: TFunction,
  summary: string,
  parts: ITriggeredAlertSummaryParts | null | undefined
): string {
  if (!parts) return summary;

  if (renderSummary(persistedCopyT, parts) !== summary) return summary;

  return renderSummary(t, parts) ?? summary;
}
